import { useState } from "react";
import { X, Loader2, Lock, User as UserIcon, Crown, Smartphone, CreditCard, QrCode, BadgeCheck, Check } from "lucide-react";
import { toast } from "sonner";
import { apiViewer, formatApiError } from "@/lib/api";

const INPUT_CLS =
  "w-full rounded-xl border border-stone-700/70 bg-ink-900/80 px-4 py-3 text-sm text-stone-100 placeholder:text-stone-500 transition-colors focus:border-gold-500/60 focus:outline-none focus:ring-1 focus:ring-gold-500/40";
const LABEL_CLS = "mb-2 block text-[10px] uppercase tracking-[0.3em] text-stone-400";

const BENEFITS = ["Ad-free streaming", "All Premium & Exclusive shows", "HD playback", "Watch on any device"];

export default function SubscribeModal({ user, onClose, onSubscribed, requireAuth }) {
  const [method, setMethod] = useState("upi");
  const [stage, setStage] = useState("details");

  const pay = () => {
    if (!user) {
      onClose();
      requireAuth("Sign in to subscribe to VIEWNA Premium");
      return;
    }
    if (stage === "processing") return;
    setStage("processing");
    setTimeout(() => {
      apiViewer
        .post("/subscription/subscribe", { plan: "premium" })
        .then(() => setStage("success"))
        .then(() => new Promise((resolve) => setTimeout(resolve, 1600)))
        .then(() => onSubscribed())
        .catch((err) => {
          toast.error(formatApiError(err.response?.data?.detail));
          setStage("details");
        });
    }, 1800);
  };

  const methodBtn = (key, Icon, label) => (
    <button
      type="button"
      data-testid={`subscribe-method-${key}`}
      onClick={() => setMethod(key)}
      className={`flex flex-col items-center gap-2 rounded-xl border px-3 py-3.5 text-[10px] font-bold uppercase tracking-[0.14em] transition-all duration-300 ${
        method === key
          ? "border-gold-500/50 bg-gold-500/15 text-gold-300"
          : "border-stone-800 text-stone-400 hover:border-gold-500/30 hover:text-gold-300"
      }`}
    >
      <Icon className="h-5 w-5" /> {label}
    </button>
  );

  return (
    <div data-testid="subscribe-modal" className="fixed inset-0 z-[110] flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div
        className="absolute inset-0 bg-black/90 backdrop-blur-md"
        data-testid="subscribe-modal-backdrop"
        onClick={stage === "processing" ? undefined : onClose}
      />
      <div className="relative max-h-[92vh] w-full max-w-md overflow-y-auto rounded-2xl border border-gold-500/30 bg-[#0c0a07] p-8 shadow-[0_0_100px_rgba(212,175,55,0.2)] sm:p-10">
        {stage === "success" ? (
          <div data-testid="subscribe-success" className="flex flex-col items-center gap-4 py-10 text-center">
            <BadgeCheck className="h-16 w-16 text-gold-400" />
            <h3 className="font-display text-2xl font-bold uppercase tracking-[0.2em] text-gold-300">
              Welcome to VIEWNA Premium!
            </h3>
            <p className="text-sm text-stone-400">Ad-free streaming · All Premium shows · HD playback</p>
          </div>
        ) : (
          <>
            <button
              data-testid="subscribe-modal-close-button"
              aria-label="Close"
              onClick={onClose}
              disabled={stage === "processing"}
              className="absolute right-5 top-5 rounded-full border border-stone-700 p-2 text-stone-300 transition-colors hover:border-gold-500/60 hover:text-gold-400 disabled:opacity-40"
            >
              <X className="h-4 w-4" />
            </button>

            <span className="flex h-14 w-14 items-center justify-center rounded-full border border-gold-500/40 bg-gold-500/10 text-gold-400">
              <Crown className="h-6 w-6" />
            </span>
            <p className="mt-5 text-[10px] font-semibold uppercase tracking-[0.4em] text-gold-500">VIEWNA Premium</p>
            <h2 className="mt-2 font-display text-3xl font-black text-stone-50">
              ₹199<span className="text-base font-medium text-stone-400"> /month</span>
            </h2>

            <ul className="mt-6 space-y-3" data-testid="premium-benefits">
              {BENEFITS.map((benefit) => (
                <li key={benefit} className="flex items-center gap-3 text-sm text-stone-300">
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-gold-500/15 text-gold-400">
                    <Check className="h-3 w-3" />
                  </span>
                  {benefit}
                </li>
              ))}
            </ul>

            <p className="mt-7 mb-3 text-[10px] uppercase tracking-[0.3em] text-stone-400">Payment Method (Demo)</p>
            <div className="grid grid-cols-3 gap-2">
              {methodBtn("upi", Smartphone, "UPI")}
              {methodBtn("card", CreditCard, "Card")}
              {methodBtn("qr", QrCode, "QR Code")}
            </div>

            <div className="mt-4">
              {method === "upi" && (
                <input data-testid="subscribe-upi-input" placeholder="yourname@upi (demo)" className={INPUT_CLS} />
              )}
              {method === "card" && (
                <div className="grid grid-cols-2 gap-3">
                  <input data-testid="subscribe-card-number-input" placeholder="Card number (demo)" className={`${INPUT_CLS} col-span-2`} />
                  <input placeholder="MM / YY" className={INPUT_CLS} />
                  <input placeholder="CVV" className={INPUT_CLS} />
                </div>
              )}
              {method === "qr" && (
                <div data-testid="subscribe-qr-placeholder" className="flex flex-col items-center gap-2 rounded-xl border border-stone-800 bg-ink-900 p-6">
                  <QrCode className="h-16 w-16 text-gold-400" />
                  <p className="text-xs text-stone-500">Scan with any UPI app — demo QR</p>
                </div>
              )}
            </div>

            <button
              data-testid="subscribe-pay-button"
              onClick={pay}
              disabled={stage === "processing"}
              className="mt-6 inline-flex w-full items-center justify-center gap-3 rounded-full bg-gradient-to-r from-gold-600 via-gold-400 to-gold-600 px-8 py-4 text-[13px] font-bold uppercase tracking-[0.2em] text-ink-950 transition-all duration-300 hover:shadow-[0_0_28px_rgba(212,175,55,0.4)] disabled:opacity-60"
            >
              {stage === "processing" && <Loader2 className="h-4 w-4 animate-spin" />}
              {stage === "processing" ? "Processing payment..." : "Subscribe Now — ₹199/month"}
            </button>
            <p className="mt-4 text-center text-[10px] text-stone-600">Demo checkout — no real money is charged</p>
          </>
        )}
      </div>
    </div>
  );
}
